import { Capacitor } from '@capacitor/core';

/**
 * 음성 전사 (STT, v1.19) — 관찰메모를 말로 남긴다 (§5.14).
 *
 * 네이티브 앱은 기기 음성 인식(Capacitor SpeechRecognition)을,
 * 웹은 브라우저 Web Speech API를 쓴다. 오디오는 서버로 보내지 않는다 —
 * 인식은 기기·브라우저 안에서 끝나고 글자만 화면에 남는다.
 */

export interface DictationHandle {
  stop: () => Promise<void>;
}

interface WebSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
}

function webSpeechCtor(): (new () => WebSpeechRecognition) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => WebSpeechRecognition;
    webkitSpeechRecognition?: new () => WebSpeechRecognition;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** 이 기기에서 음성 입력을 쓸 수 있는지 — 버튼 노출 여부를 정한다 */
export async function isDictationAvailable(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { SpeechRecognition } = await import('@capacitor-community/speech-recognition');
      return (await SpeechRecognition.available()).available;
    } catch {
      return false;
    }
  }
  return webSpeechCtor() !== null;
}

/**
 * 받아쓰기 시작 — 확정된 문장이 생길 때마다 onText로 넘긴다.
 * onEnd는 인식이 스스로 멈추거나 stop()이 불렸을 때 한 번 호출된다.
 */
export async function startDictation(
  onText: (text: string) => void,
  onEnd: (error?: string) => void,
): Promise<DictationHandle> {
  if (Capacitor.isNativePlatform()) {
    const { SpeechRecognition } = await import('@capacitor-community/speech-recognition');

    const permission = await SpeechRecognition.requestPermissions();
    if (permission.speechRecognition !== 'granted') {
      onEnd('마이크 사용 권한이 필요해요. 설정에서 허용해 주세요.');
      return { stop: async () => {} };
    }

    // partialResults 이벤트는 누적 문자열을 준다 — 마지막 확정본만 쓰기 위해 보관
    let latest = '';
    const listener = await SpeechRecognition.addListener('partialResults', (data) => {
      const text = data.matches?.[0];
      if (text) latest = text;
    });

    let stopped = false;
    const finish = async (error?: string) => {
      if (stopped) return;
      stopped = true;
      await listener.remove();
      if (latest.trim()) onText(latest.trim());
      onEnd(error);
    };

    SpeechRecognition.addListener('listeningState', (state) => {
      if (state.status === 'stopped') void finish();
    }).catch(() => undefined);

    await SpeechRecognition.start({
      language: 'ko-KR',
      partialResults: true,
      popup: false,
    });

    return {
      stop: async () => {
        try {
          await SpeechRecognition.stop();
        } finally {
          await finish();
        }
      },
    };
  }

  // ── 웹 폴백 — Web Speech API ──────────────────────────────
  const Ctor = webSpeechCtor();
  if (!Ctor) {
    onEnd('이 브라우저는 음성 입력을 지원하지 않아요.');
    return { stop: async () => {} };
  }

  const recognition = new Ctor();
  recognition.lang = 'ko-KR';
  recognition.continuous = true;
  recognition.interimResults = false;

  recognition.onresult = (event) => {
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      if (result.isFinal && result[0]?.transcript.trim()) {
        onText(result[0].transcript.trim());
      }
    }
  };
  let errored = false;
  recognition.onerror = (event) => {
    errored = true;
    onEnd(
      event.error === 'not-allowed'
        ? '마이크 사용 권한이 필요해요. 브라우저에서 허용해 주세요.'
        : '음성을 알아듣지 못했어요. 다시 시도해 주세요.',
    );
  };
  recognition.onend = () => {
    if (!errored) onEnd();
  };
  recognition.start();

  return {
    stop: async () => recognition.stop(),
  };
}
